import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { SnackbarContext } from "@/context/SnackbarProvider";
import IconLoader from "@/features/Common/components/IconLoader";
import { Box, Button } from "@mui/material";
import { WellnessCategoryScoreSection } from "../Common/WellnessCategoryScoreSection";
import WellnessCategoryCard from "../WellnessCategoryCard";
import { useTranslation } from "react-i18next";
import HealthScoreIndicator from "../Common/HealthScoreIndicator";
import CollapsableMetricsList from "../Common/CollapsableMetricsList";
import {
  WellnessCategoryCardProps,
  WellnessScoreStatus,
  getWellnessScoreStatusFromScore,
} from "../Common/types";
import { status as statusKeys } from "../Common/types";
import { useMachine } from "@xstate/react";
import {
  healthHistoryMachine,
  healthHistoryEventNames,
  HealthHistoryState,
} from "./machine";
import BMIScreen from "./BMI/BMIScreen";
import BMIUpdateForm from "./BMI/BMIUpdateForm";
import type { BMIChartDataPoint } from "./BMI/BMIChart";
import { MetricItem } from "../Common/types";
import { getBmiStatus, getBmiDisplayValue } from "./BMI/BMIInfoCard/utils";
import HealthHistoryQuestionnaire from "@/features/HealthScore/pages/HealthHistoryQuestionnaire";
import { submitHealthProfileEntry } from "@/features/HealthScore/utils/submitHealthProfileEntry";
import HealthHistoryFlyUpModal from "./HealthHistoryFlyUpModal";
import useHealthTrends from "@/features/HealthScore/hooks/useHealthTrends";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import { mapHealthTrendToBmiChartData } from "@/features/HealthScore/utils/healthTrendsChartData";
import type { HealthProfileSuccessResponseData } from "@/services/core-api-adapter";
import { getHealthHistoryMetricsFromHealthProfile } from "./utils/getHealthHistoryMetricsFromHealthProfile";
import { wellnessCategoryFullScreenOverlaySx } from "../Common/wellnessCategoryFullScreenOverlaySx";

const healthHistoryFullScreenOverlaySx = {
  ...wellnessCategoryFullScreenOverlaySx,
  paddingBottom: 5,
};

function getStatusKey(wellnessStatus: WellnessScoreStatus): string {
  switch (wellnessStatus) {
    case WellnessScoreStatus.Healthy:
      return statusKeys.healthy;
    case WellnessScoreStatus.NeedsAttention:
      return statusKeys.needAttention;
    case WellnessScoreStatus.NeedsSupport:
      return statusKeys.needsSupport;
    default:
      return statusKeys.healthy;
  }
}

export interface HealthHistoryCategoryProps {
  bmi?: number | null;
  chartData?: BMIChartDataPoint[];
  weightKg?: number;
  heightCm?: number;
  familyHistoryCount?: number;
  chronicDiseasesCount?: number;
  hivTb?: boolean | null;
  lastUpdated?: string;
  healthProfile?: HealthProfileSuccessResponseData | null;
  refetch?: () => void;
  domainScore?: number;
  domainCompleteness?: number;
  domainLastUpdated?: string;
}

export default function HealthHistoryCategory({
  bmi: bmiProp,
  chartData: chartDataProp,
  weightKg = 0,
  heightCm = 0,
  familyHistoryCount: familyHistoryCountProp = 3,
  chronicDiseasesCount: chronicDiseasesCountProp = 1,
  hivTb: hivTbProp = false,
  lastUpdated,
  healthProfile,
  refetch,
  domainScore,
  domainCompleteness,
  domainLastUpdated,
}: HealthHistoryCategoryProps = {}) {
  const { t } = useTranslation();
  const { showSnackbar } = useContext(SnackbarContext);
  const [state, send] = useMachine(healthHistoryMachine);
  const healthHistorySectionRef = useRef<HTMLDivElement>(null);
  const prevStateRef = useRef(state.value);
  const [listExpanded, setListExpanded] = useState(false);
  const [isFlyUpModalOpen, setIsFlyUpModalOpen] = useState(false);

  const fromProfile =
    healthProfile != null
      ? getHealthHistoryMetricsFromHealthProfile(
          healthProfile,
          t as (key: string) => string
        )
      : null;

  const { data: bmiTrendData, refetch: refetchBmi } =
    useHealthTrends("BodyMassIndex");
  const bmiChartDataFromApi =
    bmiTrendData && bmiTrendData.length > 0
      ? mapHealthTrendToBmiChartData(bmiTrendData)
      : null;

  const chartData = chartDataProp ?? bmiChartDataFromApi ?? [];
  const currentPoint =
    chartData.find((p) => p.isSelected) ?? chartData[chartData.length - 1];
  const bmiFromChart = currentPoint?.bmi ?? 0;
  const bmi = bmiProp ?? fromProfile?.bmi ?? bmiFromChart ?? 0;
  const hasData =
    domainScore != null && domainCompleteness != null && domainCompleteness > 0;

  const bmiStatus = getBmiStatus(bmi);

  const familyHistoryCount =
    fromProfile?.familyHistoryCount ?? familyHistoryCountProp;
  const chronicDiseasesCount =
    fromProfile?.chronicDiseasesCount ?? chronicDiseasesCountProp;
  const hivTb = fromProfile?.hivTb ?? hivTbProp;

  const resolvedWeightKg = fromProfile?.weightKg || weightKg;
  const resolvedHeightCm = fromProfile?.heightCm || heightCm;

  const metrics: MetricItem[] = fromProfile?.metrics ?? [
    {
      id: "familyHistory",
      title: t("HealthHistory.Category.metrics.familyHistory"),
      value: `${familyHistoryCount} ${familyHistoryCount === 1 ? "condition" : "conditions"}`,
      status: getStatusKey(WellnessScoreStatus.NeedsAttention),
      showChevron: false,
    },
    {
      id: "chronicDiseases",
      title: t("HealthHistory.Category.metrics.chronicDiseases"),
      value: `${chronicDiseasesCount} ${chronicDiseasesCount === 1 ? "condition" : "conditions"}`,
      status: getStatusKey(WellnessScoreStatus.NeedsAttention),
      showChevron: false,
    },
    {
      id: "hivTb",
      title: t("HealthHistory.Category.metrics.hivTb"),
      value: hivTb ? "Yes" : "No",
      status: getStatusKey(
        hivTb ? WellnessScoreStatus.NeedsSupport : WellnessScoreStatus.Healthy
      ),
      showChevron: false,
    },
    {
      id: "bmi",
      title: t("HealthHistory.Category.metrics.bmi"),
      value: getBmiDisplayValue(bmi),
      status: getStatusKey(bmiStatus),
      showChevron: true,
    },
  ];

  useEffect(() => {
    const wasBmiScreen = prevStateRef.current === HealthHistoryState.BMIScreen;
    const wasBmiForm =
      prevStateRef.current === HealthHistoryState.BMIUpdateForm;
    const wasQuestionnaire =
      prevStateRef.current === HealthHistoryState.Questionnaire;
    if (
      (wasBmiScreen || wasBmiForm || wasQuestionnaire) &&
      state.matches(HealthHistoryState.Dashboard)
    ) {
      setListExpanded(true);
      requestAnimationFrame(() => {
        const el = healthHistorySectionRef.current;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    }
    prevStateRef.current = state.value;
  }, [state.value]);

  const handleBack = () => {
    send({ type: healthHistoryEventNames.BACK });
  };

  const handleMetricClick = (metric: MetricItem) => {
    if (metric.id === "bmi") {
      send({ type: healthHistoryEventNames.OPEN_BMI });
    }
  };

  const handleUpdateHealthHistory = () => {
    setIsFlyUpModalOpen(true);
  };

  const handleFlyUpModalClose = () => {
    setIsFlyUpModalOpen(false);
  };

  const handleFlyUpUpdateBmi = () => {
    send({ type: healthHistoryEventNames.OPEN_BMI_FORM });
  };

  const handleFlyUpUpdateClinicalHistory = () => {
    send({ type: healthHistoryEventNames.OPEN_QUESTIONNAIRE });
  };

  const indicatorScoreFromStatus =
    domainScore !== undefined
      ? domainScore
      : (() => {
          switch (bmiStatus) {
            case WellnessScoreStatus.NeedsSupport:
              return 25;
            case WellnessScoreStatus.NeedsAttention:
              return 50;
            case WellnessScoreStatus.Healthy:
            default:
              return 80;
          }
        })();

  const resolvedStatus =
    domainScore != null || domainCompleteness != null
      ? getWellnessScoreStatusFromScore(domainScore ?? 0, domainCompleteness)
      : undefined;

  const healthHistoryProps: WellnessCategoryCardProps = {
    icon: (
      <IconLoader
        icon="HealthHistoryIcon"
        color="primary"
        sx={{ fontSize: 40 }}
      />
    ),
    title: t("HealthHistory.Category.title"),
    status: hasData ? resolvedStatus : undefined,
    description: hasData
      ? t("HealthHistory.description.update")
      : t("HealthHistory.description"),
    lastUpdated: domainLastUpdated,
  };

  const handleBmiSave = useCallback(
    async (weightKg: number, heightCm: number, heightUnit: "cm" | "m") => {
      const heightValue =
        heightUnit === "cm"
          ? String(heightCm)
          : String((heightCm / 100).toFixed(2));
      const heightApiUnit = heightUnit === "cm" ? "CM" : "M";
      try {
        await submitHealthProfileEntry("Weight", String(weightKg));
        await submitHealthProfileEntry("Height", heightValue, {
          unit: heightApiUnit,
        });
        if (refetch) {
          await refetch();
        }
        await refetchBmi();
        send({ type: healthHistoryEventNames.BACK_TO_DASHBOARD });
      } catch {
        showSnackbar({
          message: t(
            "Metric.manualEntry.updateError",
            "Unable to save. Please try again."
          ),
          animate: true,
          type: "error",
          direction: "down",
          position: { horizontal: "center", vertical: "top" },
        });
      }
    },
    [refetch, refetchBmi, send, showSnackbar, t]
  );

  const handleMetricUpdated = useCallback(() => {
    refetch?.();
    refetchBmi();
  }, [refetch, refetchBmi]);

  const [isBmiSaving, setIsBmiSaving] = useState(false);
  const handleBmiSaveWithLoading = useCallback(
    async (weightKg: number, heightCm: number, heightUnit: "cm" | "m") => {
      setIsBmiSaving(true);
      try {
        await handleBmiSave(weightKg, heightCm, heightUnit);
      } finally {
        setIsBmiSaving(false);
      }
    },
    [handleBmiSave]
  );

  if (state.matches(HealthHistoryState.Questionnaire)) {
    return (
      <Box sx={healthHistoryFullScreenOverlaySx}>
        <HealthHistoryQuestionnaire
          onClose={() =>
            send({ type: healthHistoryEventNames.BACK_TO_DASHBOARD })
          }
        />
      </Box>
    );
  }

  if (state.matches(HealthHistoryState.BMIScreen)) {
    return (
      <Box sx={healthHistoryFullScreenOverlaySx}>
        <BMIScreen
          chartData={chartData}
          weightKg={resolvedWeightKg}
          heightCm={resolvedHeightCm}
          lastUpdated={lastUpdated}
          onBack={handleBack}
          onMetricUpdated={handleMetricUpdated}
        />
      </Box>
    );
  }

  if (state.matches(HealthHistoryState.BMIUpdateForm)) {
    if (isBmiSaving) {
      return (
        <Box sx={healthHistoryFullScreenOverlaySx}>
          <FullscreenLoadingIndicator mainIconType="face" />
        </Box>
      );
    }
    return (
      <Box sx={healthHistoryFullScreenOverlaySx}>
        <BMIUpdateForm
          onBack={() => send({ type: healthHistoryEventNames.BACK })}
          onSave={handleBmiSaveWithLoading}
        />
      </Box>
    );
  }

  return (
    <Box ref={healthHistorySectionRef}>
      <WellnessCategoryCard {...healthHistoryProps}>
        {hasData && (
          <WellnessCategoryScoreSection>
            <HealthScoreIndicator healthScore={indicatorScoreFromStatus} />

            <CollapsableMetricsList
              metrics={metrics}
              onMetricClick={handleMetricClick}
              expanded={listExpanded}
              onExpandedChange={setListExpanded}
            />
          </WellnessCategoryScoreSection>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleUpdateHealthHistory}
          fullWidth
        >
          {hasData
            ? t("HealthHistory.buttonLabel.primary.update")
            : t("HealthHistory.buttonLabel.primary")}
        </Button>
      </WellnessCategoryCard>

      <HealthHistoryFlyUpModal
        isOpen={isFlyUpModalOpen}
        onClose={handleFlyUpModalClose}
        onUpdateBmi={handleFlyUpUpdateBmi}
        onUpdateClinicalHistory={handleFlyUpUpdateClinicalHistory}
      />
    </Box>
  );
}
